// vehicleOnboardingApi.js - API utilities for vehicle onboarding operations

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Helper function to extract pure Base64 data from image data URLs
 * Removes the "data:image/jpeg;base64," prefix if present
 */
const extractBase64Data = imageData => {
  if (!imageData) return null;

  // If it's already a pure Base64 string, return as is
  if (!imageData.includes('data:')) {
    return imageData;
  }

  // Extract the Base64 part after the comma
  const base64Match = imageData.match(/data:image\/[^;]+;base64,(.+)/);
  if (base64Match) {
    return base64Match[1];
  }

  return imageData;
};

/**
 * Function to get the authentication token.
 * This function dynamically fetches the ID Token from AWS Amplify's Auth module.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens && tokens.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (idToken.payload.exp <= currentTime) {
        console.error('ID Token has expired. User needs to re-authenticate.');
        return null;
      }

      console.log(
        'Successfully retrieved valid ID token for vehicle onboarding API'
      );
      return tokens.idToken.toString();
    } else {
      console.error(
        'No ID Token found in the current session. User may not be logged in.'
      );
      return null;
    }
  } catch (error) {
    console.error('Error fetching authentication session:', error);
    return null;
  }
};

/**
 * Vehicle Onboarding API
 * Creates a new vehicle record
 * Endpoint: /driver/vehicle/create (now available on Stage)
 */
export const createVehicle = async vehicleData => {
  try {
    const idToken = await getAuthToken();
    if (!idToken) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(
      'Successfully retrieved valid ID token for vehicle onboarding API'
    );

    // Use the correct endpoint that matches Flutter implementation
    const endpoint = `${API_BASE_URL}/driver/vehicle/create`;
    console.log('Calling endpoint:', endpoint);
    console.log('Using token:', idToken.substring(0, 20) + '...');

    // Prepare payload matching Flutter structure (without tenant_id)
    const requestBody = {
      registration_no: vehicleData.registration_no,
      area_available: vehicleData.area_available,
      model_name: vehicleData.model_name,
      vehicle_type: vehicleData.vehicle_type,
      engine_cc: vehicleData.engine_cc,
      vehicle_color: vehicleData.vehicle_color,
      seating_capacity: vehicleData.seating_capacity,
      cargo_capacity: vehicleData.cargo_capacity,
      base_location: vehicleData.base_location,
      imageData: vehicleData.imageData
        ? extractBase64Data(vehicleData.imageData)
        : null,
    };

    console.log('Request Body:', requestBody);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      error.endpoint = '/driver/vehicle/create';
      error.responseData = errorText;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /driver/vehicle/create`,
        error,
        {
          endpoint: '/driver/vehicle/create',
          method: 'POST',
          status: response.status,
          responseData: errorText,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Vehicle created successfully:', result);
    return result;
  } catch (error) {
    console.error('Error creating vehicle:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /driver/vehicle/create`,
        error,
        {
          endpoint: '/driver/vehicle/create',
          method: 'POST',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get All Vehicles API
 * Fetches all vehicles for the current user's tenant
 * Endpoint: /vehicle/getall
 */
export const getAllVehicles = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/vehicle/getall`);

    const response = await fetch(`${API_BASE_URL}/vehicle/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/vehicle/getall';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /vehicle/getall`,
        error,
        {
          endpoint: '/vehicle/getall',
          method: 'GET',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Vehicles fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /vehicle/getall`,
        error,
        {
          endpoint: '/vehicle/getall',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Vehicle by ID API
 * Fetches a specific vehicle by its ID
 * Endpoint: /driver/vehicle?vehicle_id={vehicleId}
 */
export const getVehicleById = async vehicleId => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const url = `${API_BASE_URL}/driver/vehicle?vehicle_id=${vehicleId}`;
    console.log(`Calling endpoint: ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 200) {
      const result = await response.json();
      console.log('Vehicle fetched successfully:', result);
      return result;
    } else if (response.status === 404) {
      const error = new Error(`Vehicle with ID ${vehicleId} not found.`);
      error.status = response.status;
      error.endpoint = '/driver/vehicle';
      
      // Log API error to logging service (including 404s as user requested)
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /driver/vehicle`,
        error,
        {
          endpoint: '/driver/vehicle',
          method: 'GET',
          status: response.status,
          vehicleId: vehicleId,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    } else if (response.status === 400) {
      const error = new Error('Vehicle ID and Tenant ID are required.');
      error.status = response.status;
      error.endpoint = '/driver/vehicle';
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /driver/vehicle`,
        error,
        {
          endpoint: '/driver/vehicle',
          method: 'GET',
          status: response.status,
          vehicleId: vehicleId,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    } else {
      const responseText = await response.text();
      const error = new Error(
        `Failed to fetch vehicle details: ${response.status} - ${responseText}`
      );
      error.status = response.status;
      error.endpoint = '/driver/vehicle';
      error.responseData = responseText;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /driver/vehicle`,
        error,
        {
          endpoint: '/driver/vehicle',
          method: 'GET',
          status: response.status,
          vehicleId: vehicleId,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }
  } catch (error) {
    console.error('Error fetching vehicle:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /driver/vehicle`,
        error,
        {
          endpoint: '/driver/vehicle',
          method: 'GET',
          errorType: error.name || 'NetworkError',
          vehicleId: vehicleId,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Update Vehicle API
 * Updates an existing vehicle record
 * Endpoint: /vehicle/update/{id}
 */
export const updateVehicle = async (vehicleId, vehicleData) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(
      `Calling endpoint: ${API_BASE_URL}/vehicle/update/${vehicleId}`
    );
    console.log('Request Body:', JSON.stringify(vehicleData, null, 2));

    const response = await fetch(
      `${API_BASE_URL}/vehicle/update/${vehicleId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(vehicleData),
      }
    );

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = `/vehicle/update/${vehicleId}`;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /vehicle/update/${vehicleId}`,
        error,
        {
          endpoint: `/vehicle/update/${vehicleId}`,
          method: 'PUT',
          status: response.status,
          vehicleId: vehicleId,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Vehicle updated successfully:', result);
    return result;
  } catch (error) {
    console.error('Error updating vehicle:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /vehicle/update/${vehicleId}`,
        error,
        {
          endpoint: `/vehicle/update/${vehicleId}`,
          method: 'PUT',
          errorType: error.name || 'NetworkError',
          vehicleId: vehicleId,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Delete Vehicle API
 * Deletes a vehicle record
 * Endpoint: /vehicle/delete/{id}
 */
export const deleteVehicle = async vehicleId => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(
      `Calling endpoint: ${API_BASE_URL}/vehicle/delete/${vehicleId}`
    );

    const response = await fetch(
      `${API_BASE_URL}/vehicle/delete/${vehicleId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = `/vehicle/delete/${vehicleId}`;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /vehicle/delete/${vehicleId}`,
        error,
        {
          endpoint: `/vehicle/delete/${vehicleId}`,
          method: 'DELETE',
          status: response.status,
          vehicleId: vehicleId,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Vehicle deleted successfully:', result);
    return result;
  } catch (error) {
    console.error('Error deleting vehicle:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /vehicle/delete/${vehicleId}`,
        error,
        {
          endpoint: `/vehicle/delete/${vehicleId}`,
          method: 'DELETE',
          errorType: error.name || 'NetworkError',
          vehicleId: vehicleId,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get Vehicle Types API
 * Returns fallback vehicle types since /vehicle/types endpoint doesn't exist
 */
export const getVehicleTypes = async () => {
  // Return fallback vehicle types since the /vehicle/types endpoint doesn't exist
  return [
    {
      id: 'Light',
      name: 'Light',
      description: 'Light vehicles (cars, motorcycles, small vans)',
    },
    {
      id: 'Medium',
      name: 'Medium',
      description: 'Medium vehicles (SUVs, pickup trucks, medium vans)',
    },
    {
      id: 'Heavy',
      name: 'Heavy',
      description: 'Heavy vehicles (trucks, buses, tractors)',
    },
  ];
};

/**
 * Get Sites/Locations API
 * Fetches available sites/locations from the system
 * Endpoint: /site/getall
 */
export const getSites = async () => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(
        'Authentication token is missing. Please ensure a user is logged in.'
      );
    }

    console.log(`Calling endpoint: ${API_BASE_URL}/site/getall`);

    const response = await fetch(`${API_BASE_URL}/site/getall`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const responseText = await response.text();
      console.log(`HTTP Error: ${response.status} ${response.statusText}`);
      console.log(`Response Body:`, responseText);

      let errorMessage;
      let errorData = {};
      try {
        errorData = JSON.parse(responseText);
        errorMessage =
          errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`;
      } catch {
        errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.endpoint = '/site/getall';
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API error: ${response.status} for /site/getall`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          status: response.status,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    const result = await response.json();
    console.log('Sites fetched successfully:', result);
    return result;
  } catch (error) {
    console.error('Error fetching sites:', error);
    
    // Log network/fetch errors to logging service
    if (!error.status) {
      loggingService.logError(
        'api_error_vehicle_onboarding',
        `Vehicle Onboarding API network/fetch error for /site/getall`,
        error,
        {
          endpoint: '/site/getall',
          method: 'GET',
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[vehicleOnboardingApi] Failed to log error:', logError);
      });
    }
    
    // Return fallback sites if API fails
    return [
      {
        site_id: 'site_001',
        site_name: 'Bangalore Main Office',
        site_address: 'Bangalore, Karnataka',
      },
      {
        site_id: 'site_002',
        site_name: 'Mumbai Branch',
        site_address: 'Mumbai, Maharashtra',
      },
      {
        site_id: 'site_003',
        site_name: 'Delhi Office',
        site_address: 'Delhi, NCR',
      },
    ];
  }
};

/**
 * Validate Vehicle Data
 * Validates vehicle data before sending to API
 */
export const validateVehicleData = vehicleData => {
  const errors = [];
  const requiredFields = [
    'registration_no',
    'vehicle_type',
    'vehicle_color',
    'base_location',
  ];

  requiredFields.forEach(field => {
    if (!vehicleData[field] || vehicleData[field].toString().trim() === '') {
      errors.push(
        `${field
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()} is required`
      );
    }
  });

  // Validate registration number format (basic validation)
  if (vehicleData.registration_no) {
    const regNumberRegex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/;
    if (!regNumberRegex.test(vehicleData.registration_no)) {
      errors.push('Registration number format is invalid (e.g., KA01AB1234)');
    }
  }

  if (vehicleData.cargo_capacity) {
    const cargoValue = vehicleData.cargo_capacity.toString().trim();
    if (!/^\d+$/.test(cargoValue)) {
      errors.push('Cargo capacity must be a numeric value in kg');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default {
  createVehicle,
  getAllVehicles,
  getVehicleById,
  updateVehicle,
  deleteVehicle,
  getVehicleTypes,
  getSites,
  validateVehicleData,
};
